import { getBlankUnitMeta } from "./fillQuestionUtils";

// Answer-unit chip next to the typed box (W, Watts, cals, RF, feet, £…). Populated by the API from
// ExtractsAnswerUnit / AnswerUnitVocabulary — the take pages only display it. Kept here rather than
// copied per page: the two copies had already drifted apart on compound support.
export const resolveAnswerUnitProps = (question, enabled) => {
  if (!enabled) return { unitSuffix: undefined, unitPrefix: undefined };
  const unit = question?.answer_data?.answer_unit;
  if (!unit) return { unitSuffix: undefined, unitPrefix: undefined };
  const isPrefix = question?.answer_data?.answer_unit_prefix === true;
  return {
    unitSuffix: isPrefix ? undefined : unit,
    unitPrefix: isPrefix ? unit : undefined,
  };
};

export const resolveBlankOrAnswerUnitProps = (question, enabled, blankIdx = 0) => {
  if (!enabled) return { unitSuffix: undefined, unitPrefix: undefined };
  const u = getBlankUnitMeta(question, blankIdx);
  if (u.unit) {
    return {
      unitSuffix: u.prefix ? undefined : u.unit,
      unitPrefix: u.prefix ? u.unit : undefined,
    };
  }
  return resolveAnswerUnitProps(question, enabled);
};

// "6 minutes 24 seconds" wears two units, so it gets one numeric box per unit rather than a single
// box with one chip. Returns null unless there are at least two.
export const resolveCompoundUnits = (question, enabled) => {
  if (!enabled) return null;
  const units = question?.answer_data?.answer_compound_units;
  return Array.isArray(units) && units.length > 1 ? units : null;
};

// A currency member of a compound is written before its value ("£22 60p"), not after it.
export const CURRENCY_UNITS = ["£", "$", "€", "¥"];

const escapeUnit = (u) => String(u).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const matchUnitValue = (segment, unit) => {
  const u = escapeUnit(unit);
  const m = CURRENCY_UNITS.includes(unit)
    ? String(segment ?? "").match(new RegExp(`${u}\\s*(\\d+(?:\\.\\d+)?)`))
    : String(segment ?? "").match(new RegExp(`(\\d+(?:\\.\\d+)?)\\s*${u}`, "i"));
  return m ? m[1] : "";
};

// Parts are joined with " | " (see combineCompoundAnswer) so each box's value is isolated by
// position, not re-derived by scanning the whole string per unit — a whole-string scan let the
// pounds digits (followed by whitespace, then eventually "p") get matched into the pence box too,
// duplicating the value and making the pence box un-erasable. Old stored answers predating the
// delimiter fall back to the whole-string scan.
export const parseCompoundParts = (value, units) => {
  const raw = String(value ?? "");
  if (!raw) return units.map(() => "");
  const segments = raw.split("|");
  if (segments.length === units.length) {
    return segments.map((seg, i) => matchUnitValue(seg, units[i]));
  }
  return units.map((unit) => matchUnitValue(raw, unit));
};

// Compound units inside a FILL BLANK cannot use the " | " delimiter above: a multi-fill submission
// already joins its BLANKS with " | ", so a pipe inside one blank shifts every later blank out of
// alignment when the server splits them. Blanks write the plain mark-scheme form instead
// ("8 minutes 34 seconds"), and read it back by scanning each unit in turn from where the previous one
// ended — sequential, so the pounds digits of "£22 60p" can never also land in the pence box.
export const parseBlankCompoundParts = (value, units) => {
  const raw = String(value ?? "");
  const out = units.map(() => "");
  if (!raw) return out;
  let pos = 0;
  units.forEach((unit, i) => {
    const u = escapeUnit(unit);
    const re = CURRENCY_UNITS.includes(unit)
      ? new RegExp(`${u}\\s*(\\d+(?:\\.\\d+)?)`, "g")
      : new RegExp(`(\\d+(?:\\.\\d+)?)\\s*${u}`, "gi");
    re.lastIndex = pos;
    const m = re.exec(raw);
    if (m) {
      out[i] = m[1];
      pos = re.lastIndex;
    }
  });
  return out;
};

const valueEndsWithUnitToken = (value, unit) => {
  const v = String(value ?? "").trim();
  const u = String(unit ?? "").trim();
  if (!v || !u) return false;
  const esc = escapeUnit(u);
  return new RegExp(`${esc}(?:\\s*${esc})?\\s*$`, "i").test(v);
};

const joinValueWithUnit = (value, unit) => {
  const v = String(value ?? "").trim();
  const u = String(unit ?? "").trim();
  if (!v) return "";
  if (!u) return v;
  if (CURRENCY_UNITS.includes(u)) return `${u}${v}`;
  if (valueEndsWithUnitToken(v, u)) {
    return v.replace(new RegExp(`\\s*${escapeUnit(u)}\\s*$`, "i"), ` ${u}`).trimEnd();
  }
  return `${v} ${u}`;
};

export const combineBlankCompoundAnswer = (values, units) => {
  if (values.every((v) => String(v ?? "").trim() === "")) return "";
  return values.map((v, i) => joinValueWithUnit(v, units[i])).filter(Boolean).join(" ");
};

export const combineCompoundAnswer = (values, units) => {
  // All boxes empty is no answer at all — joining the bare unit labels ("£ p") would submit as one.
  if (values.every((v) => String(v ?? "").trim() === "")) return "";
  return values.map((v, i) => joinValueWithUnit(v, units[i])).filter(Boolean).join(" | ");
};

/** Review display: "minutesminutes" / "minutes minutes" → "minutes"; "1 h | 47 min" → readable. */
export const cleanGluedDuplicateUnits = (text) => {
  if (text == null || text === "") return text;
  let t = String(text);
  const unitWord =
    "hours?|hrs?|minutes?|mins?|seconds?|secs?|days?|weeks?|months?|years?|metres?|meters?|centimetres?|centimeters?|millimetres?|millimeters?|kilograms?|grams?|litres?|liters?|millilitres?|milliliters?|pence|kg|cm|mm|km|ml|watts?|calories?|cals?";
  t = t.replace(new RegExp(`\\b(${unitWord})(?:\\s*)?\\1\\b`, "gi"), "$1");
  t = t.replace(/\s*\|\s*/g, " and ");
  return t.replace(/\s{2,}/g, " ").trim();
};
